import { useMemo, useRef } from 'react';
import { LucideShieldCheck, LucideAward, LucideGlobe, LucideZap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';

const reputationIcons = [LucideShieldCheck, LucideGlobe, LucideAward, LucideZap];

function splitTitleParts(text = '') {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { line1: String(text || '').trim(), line2: '' };
  return {
    line1: words.slice(0, -1).join(' '),
    line2: words[words.length - 1],
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function AboutReputationSection() {
  const { t } = useI18n();
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);
  const cards = t('aboutPage.reputation.cards');

  const titleParts = useMemo(() => {
    const line1 = String(t('aboutPage.reputation.titleLine1', '') || '').trim();
    const line2 = String(t('aboutPage.reputation.titleLine2', '') || '').trim();
    if (line1 || line2) {
      return {
        line1: line1 || String(t('aboutPage.reputation.title', '') || '').trim(),
        line2,
      };
    }

    const full = String(t('aboutPage.reputation.title', '') || '').trim();
    const words = full.split(/\s+/).filter(Boolean);
    if (words.length <= 1) return { line1: full, line2: '' };
    return {
      line1: words.slice(0, -1).join(' '),
      line2: words[words.length - 1],
    };
  }, [t]);

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.10) 0%, transparent 42%)`;
      }
    });
  };

  return (
    <section
      className="relative overflow-hidden bg-[var(--dash-main)] pt-24 pb-24"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-80 bg-[url('/bg/2.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-[1]">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 40% 30%, rgba(255,93,16,0.10) 0%, transparent 42%)' }}
        />
      </div>
      <div className="pointer-events-none absolute right-0 top-0 h-[460px] w-[460px] translate-x-1/4 -translate-y-1/3 rounded-full bg-[#ff5d10]/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[420px] w-[420px] -translate-x-1/4 translate-y-1/3 rounded-full bg-blue-500/10 blur-[120px]" />

      <div className="container relative z-10 mx-auto px-6 lg:px-10">
        <motion.div
          className="mb-8 max-w-2xl"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <motion.div variants={itemVariants}>
            <AnimatedEyebrow className="mb-6">{t('aboutPage.reputation.eyebrow')}</AnimatedEyebrow>
          </motion.div>
          <motion.h2 variants={itemVariants} className="text-3xl font-light uppercase leading-tight text-white sm:text-4xl lg:text-[2.7rem]">
            <span className="block">{titleParts.line1}</span>
            {titleParts.line2 ? (
              <span className="block font-black text-white">{titleParts.line2}</span>
            ) : null}
          </motion.h2>
        </motion.div>

        <motion.div
          className="grid gap-6 md:grid-cols-2"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          {Array.isArray(cards)
            ? cards.map((item, index) => (
                (() => {
                  const titleParts = splitTitleParts(item.title);
                  return (
                <motion.article
                  key={item.title}
                  variants={itemVariants}
                  className="group relative overflow-hidden rounded-2xl border border-white/15 bg-[var(--dash-black)] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--dash-other)]/70"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <div className="relative rounded-xl p-3 transition-all duration-500">
                      <div className="absolute inset-0 rounded-xl border-2 border-white/30 transition-all duration-500 group-hover:border-[var(--dash-other)] group-hover:bg-[var(--dash-other)]" />
                      {(() => {
                        const Icon = reputationIcons[index % reputationIcons.length];
                        return (
                          <Icon className="relative z-10 h-6 w-6 text-[var(--dash-other)] transition-colors duration-500 group-hover:text-[var(--dash-black)]" />
                        );
                      })()}
                    </div>
                  </div>
                  <h3 className="text-base font-light uppercase !leading-[1.2] tracking-[0.05em] text-white">
                    {titleParts.line1}{' '}
                    {titleParts.line2 ? <span className="font-black text-[var(--dash-other)]">{titleParts.line2}</span> : null}
                  </h3>
                  <p className="mt-4 max-w-[95%] text-sm leading-relaxed text-white/85">{item.description}</p>
                </motion.article>
                  );
                })()
              ))
            : null}
        </motion.div>
      </div>
    </section>
  );
}
